import { getProduct } from "@/lib/api/products";
import {
  getProductValue,
  normalizeProduct,
  parseProductIdFromParam,
} from "@/lib/utils/product";
import { resolveCategoryBySlug } from "@/lib/utils/category";
import {
  getCategoryHref,
  getCategoryNavBreadcrumbItems,
  getProductNavBreadcrumbItems,
} from "@/lib/navigation/productCategories";
import { resolveStaticBrand } from "@/lib/navigation/brand";
import { getBlogPostBySlug } from "@/lib/api/blog";

const LABEL_MAP = {
  "/": "خانه",
  "/product": "محصولات",
  "/about-us": "درباره ما",
  "/stores": "فروشگاه‌ها",
  "/blog": "وبلاگ",
  "/brands": "برندها",
  "/categories": "دسته‌بندی‌ها",
  "/accessory": "اکسسوری",
};

/**
 * Build breadcrumb items for a pathname (server-safe).
 * @param {string} pathname
 * @returns {Promise<Array<{ label: string, href: string }>|null>}
 */
export async function getBreadcrumbItems(pathname) {
  if (!pathname || pathname === "/") return null;

  if (pathname.startsWith("/panel") || pathname.startsWith("/admin")) {
    return null;
  }

  const isProductDetail =
    pathname.startsWith("/product/") && pathname !== "/product";
  const isCategoryDetail =
    pathname.startsWith("/category/") && pathname !== "/category";
  const isBrandDetail = pathname.startsWith("/brand/");
  const isBlogDetail =
    pathname.startsWith("/blog/") && pathname !== "/blog";

  let items = getProductNavBreadcrumbItems(pathname);

  if (!items) {
    const segments = pathname.split("/").filter(Boolean);
    items = [{ label: "خانه", href: "/" }];

    let accumulated = "";
    for (const segment of segments) {
      accumulated += `/${segment}`;
      const label = LABEL_MAP[accumulated];
      if (label) {
        items.push({ label, href: accumulated });
      }
    }
  }

  if (isProductDetail) {
    const productSegment = pathname.split("/product/")[1] || "";
    const id = parseProductIdFromParam(productSegment);
    const product = id
      ? normalizeProduct(await getProduct(id).catch(() => null))
      : null;

    if (product) {
      items = [{ label: "خانه", href: "/" }];

      const cat = product.categories?.[0];
      const catSlug = cat?.title_en || cat?.slug;
      const catName = cat?.title || cat?.name || "محصولات";
      const navItems = getCategoryNavBreadcrumbItems(catSlug);

      if (navItems) {
        items.push(...navItems.slice(1));
      } else {
        items.push({
          label: catName,
          href: getCategoryHref(catSlug),
        });
      }

      const title = getProductValue(product.title) || "محصول";
      items.push({ label: title, href: pathname });
    }
  }

  if (isCategoryDetail) {
    const slug = decodeURIComponent(pathname.split("/").pop() || "");
    const category = await resolveCategoryBySlug(slug).catch(() => null);
    const catSlug = category?.title_en || category?.slug || slug;
    const navItems = getCategoryNavBreadcrumbItems(catSlug);

    if (navItems) {
      const lastLabel =
        category?.title || category?.name || navItems[navItems.length - 1].label;
      items = [
        ...navItems.slice(0, -1),
        { label: lastLabel, href: pathname },
      ];
    } else {
      items = [
        { label: "خانه", href: "/" },
        { label: "دسته‌بندی‌ها", href: "/categories" },
      ];
      if (category) {
        items.push({
          label: category.title || category.name || "دسته‌بندی",
          href: pathname,
        });
      }
    }
  }

  if (isBrandDetail) {
    items = [{ label: "خانه", href: "/" }];
    items.push({ label: "برندها", href: "/brands" });

    const slug = pathname.split("/").pop();
    const brand = resolveStaticBrand(slug);
    if (brand) {
      items.push({ label: brand.title || brand.name || "برند", href: pathname });
    }
  }

  if (isBlogDetail) {
    const slug = pathname.split("/").pop();
    const blogPost = await getBlogPostBySlug(slug).catch(() => null);

    items = [{ label: "خانه", href: "/" }];
    items.push({ label: "وبلاگ", href: "/blog" });
    if (blogPost) {
      items.push({ label: blogPost.title || "مقاله", href: pathname });
    }
  }

  return items;
}
